/* Hallmark · component: usage-dashboard-widget · genre: modern-minimal · theme: project system
 * states: default · loading · empty · error — plus hover/focus on range/view buttons and details link
 * pre-emit critique: P4 H4 E4 S4 R5 V4
 */

/**
 * UsageDashboardWidget — the console's landing dashboard (`/dashboard`).
 *
 * One full-size dashboardWidgets entry that shows WHAT the org's API is used
 * for over time: a stacked per-day bar chart. Range presets 7/30/90 days, day
 * buckets. The period total is NOT repeated here — the ring beside it
 * (TodayUsageWidget, same range) carries it as its centre value; two equally
 * large copies of the same number competed for attention.
 *
 * Colours: one hue. The four largest series of the range keep their name and
 * take the accent ramp `--chart-1..4` (index.css); everything past them folds
 * into ONE grey "Other" series — the ring's rule and the ring's label
 * (`lib/usageSeries.ts`), so a type has the same shade and name in ring, bars
 * and the billing „Usage" tab. Twelve grey series nobody could tell apart was
 * the alternative.
 *
 * Two views: by event type (default) and — once the org has 2+ API keys —
 * by API key, so multi-key orgs see which key drives the traffic. The
 * per-key series come from `billing.getUsageHistory` with `groupBy: 'apiKey'`
 * (metadata.apiKeyId, stamped by the backend's auth middleware); events
 * recorded before that instrumentation or made from the console show up as
 * one honest "unattributed" series instead of being silently dropped.
 *
 * Quota details stay on /usage (linked below) instead of being duplicated.
 */

import {
  UsageChart,
  type UsageHistoryEntry,
  useUsageHistory,
} from '@xhubio-saas/core-frontend/plugin-usage'
import { cn } from '@xhubio-saas/core-frontend/ui-components'
import { ArrowRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { trpc } from '@/trpc'
import { useApiKeyClient } from '../../../api/apiKeyClient'
import { useUsageClient } from '../../../api/usageClient'
import { useEventTypeLabel } from '../../../lib/eventTypeLabel'
import { foldUsageSeries } from '../../../lib/usageSeries'
import { CONTROL_TRANSITION, FIELD_RING } from '../../auth/authUi'
import { rangeToWindow, setDashboardRange, useDashboardRange } from './dashboardRange'
import { RANGE_PRESETS, SEGMENT_BUTTON } from './dashboardUi'

type ViewMode = 'byType' | 'byKey'

/** The org's API keys (id + name) — resolves per-key series to names and
 * decides whether the by-key view is offered at all (2+ keys). */
function useApiKeyNames() {
  const client = useApiKeyClient()
  const [keys, setKeys] = useState<Array<{ id: string; name: string }> | null>(null)
  useEffect(() => {
    let alive = true
    client
      .list()
      .then((list) => {
        if (alive) setKeys(list.map((key) => ({ id: key.id, name: key.name })))
      })
      .catch(() => {
        if (alive) setKeys([])
      })
    return () => {
      alive = false
    }
  }, [client])
  return keys
}

export function UsageDashboardWidget() {
  const { t } = useTranslation()
  // „Other" names the folded remainder exactly like the ring beside it (core usage bundle).
  const { t: tUsage } = useTranslation('usage')
  const otherLabel = tUsage('todayCard.othersLabel', 'Other')
  const client = useUsageClient()
  const eventTypeLabel = useEventTypeLabel()
  const apiKeys = useApiKeyNames()
  // Geteilt mit dem Ring-Widget daneben (siehe dashboardRange.ts) — der
  // Umschalter unten steuert beide.
  const days = useDashboardRange()
  const [view, setView] = useState<ViewMode>('byType')

  const hasMultipleKeys = (apiKeys?.length ?? 0) >= 2
  const activeView: ViewMode = hasMultipleKeys ? view : 'byType'

  // Stable range per selection — recomputed only when the preset changes, so
  // the history hook doesn't refetch on unrelated re-renders. Die Rechnung
  // selbst liegt in dashboardRange.ts, weil der Ring dasselbe Fenster braucht.
  const { startDate, endDate, granularity } = useMemo(() => rangeToWindow(days), [days])

  const byType = useUsageHistory(client, {
    startDate,
    endDate,
    granularity,
    // '*' = all event types (UsageHistoryOptions requires a pattern)
    pattern: '*',
  })

  // Per-key series.
  const byKeyInput = {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    granularity,
    groupBy: 'apiKey' as const,
  }
  const byKey = trpc.billing.getUsageHistory.useQuery(byKeyInput, {
    enabled: activeView === 'byKey',
    staleTime: 60 * 1000,
  })

  /** Label for a per-key series: key name, 'unattributed' bucket, or the
   * (revoked/unknown) key id shortened. */
  const keyLabel = useCallback(
    (seriesKey: string) => {
      if (seriesKey === 'none') return t('dashboard.unattributed', 'Ohne Zuordnung')
      const known = apiKeys?.find((key) => key.id === seriesKey)
      return known?.name ?? `${seriesKey.slice(0, 8)}…`
    },
    [apiKeys, t]
  )

  const isLoading = activeView === 'byType' ? byType.isLoading : byKey.isLoading
  const error = activeView === 'byType' ? byType.error : byKey.error

  // Normalize the active series to (date, seriesKey, count) triples.
  const activeEntries = useMemo(() => {
    if (activeView === 'byType') {
      return byType.history.map((entry) => ({
        date: entry.date,
        seriesKey: entry.pattern,
        count: entry.count,
      }))
    }
    const raw = (byKey.data ?? []) as Array<{ date: string; pattern: string; count: number }>
    return raw.map((entry) => ({
      date: new Date(entry.date),
      seriesKey: entry.pattern,
      count: entry.count,
    }))
  }, [activeView, byType.history, byKey.data])

  // Chart wants one entry per day with a breakdown → regroup, translating
  // series keys so legend/tooltips speak the UI language; then fold past the
  // four largest series into „Other" (lib/usageSeries.ts).
  const { chartData, seriesColors } = useMemo(() => {
    const labelFor = activeView === 'byType' ? eventTypeLabel : keyLabel
    const byDay = new Map<
      string,
      { date: Date; count: number; breakdown: Record<string, number> }
    >()
    for (const entry of activeEntries) {
      const dayKey =
        granularity === 'hour' ? entry.date.toISOString() : entry.date.toISOString().slice(0, 10)
      const bucket = byDay.get(dayKey) ?? { date: entry.date, count: 0, breakdown: {} }
      if (entry.count > 0) {
        bucket.count += entry.count
        const label = labelFor(entry.seriesKey)
        bucket.breakdown[label] = (bucket.breakdown[label] ?? 0) + entry.count
      }
      byDay.set(dayKey, bucket)
    }
    const perDay: UsageHistoryEntry[] = Array.from(byDay.values())
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .map((bucket) => ({ ...bucket, pattern: 'total' }))
    const folded = foldUsageSeries(perDay, otherLabel)
    return { chartData: folded.data, seriesColors: folded.seriesColors }
  }, [activeView, activeEntries, eventTypeLabel, keyLabel, granularity, otherLabel])

  return (
    <div className="flex flex-col gap-6" data-testid="usage-dashboard-widget">
      {/* Header row: subtitle · (view toggle when 2+ keys) · range selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {activeView === 'byType'
            ? t('dashboard.subtitle', 'API-Aufrufe pro Tag, nach Typ')
            : t('dashboard.subtitleByKey', 'API-Aufrufe pro Tag, nach API-Key')}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {hasMultipleKeys && (
            <fieldset
              className="flex rounded-lg border border-border p-0.5"
              data-testid="dashboard-view-toggle"
            >
              <legend className="sr-only">{t('dashboard.view.label', 'Ansicht')}</legend>
              <button
                type="button"
                onClick={() => setView('byType')}
                aria-pressed={activeView === 'byType'}
                data-testid="dashboard-view-by-type"
                className={SEGMENT_BUTTON(activeView === 'byType')}
              >
                {t('dashboard.view.byType', 'Nach Typ')}
              </button>
              <button
                type="button"
                onClick={() => setView('byKey')}
                aria-pressed={activeView === 'byKey'}
                data-testid="dashboard-view-by-key"
                className={SEGMENT_BUTTON(activeView === 'byKey')}
              >
                {t('dashboard.view.byKey', 'Nach API-Key')}
              </button>
            </fieldset>
          )}
          <fieldset className="flex rounded-lg border border-border p-0.5">
            <legend className="sr-only">{t('dashboard.range.label', 'Zeitraum')}</legend>
            <button
              type="button"
              onClick={() => setDashboardRange('today')}
              aria-pressed={days === 'today'}
              data-testid="dashboard-range-today"
              className={SEGMENT_BUTTON(days === 'today')}
            >
              {t('dashboard.range.today', 'Heute')}
            </button>
            {RANGE_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setDashboardRange(preset)}
                aria-pressed={days === preset}
                data-testid={`dashboard-range-${preset}`}
                className={SEGMENT_BUTTON(days === preset)}
              >
                {t(`dashboard.range.days${preset}`, `${preset} Tage`)}
              </button>
            ))}
          </fieldset>
        </div>
      </div>

      {/* Chart / loading / empty / error */}
      {isLoading ? (
        <div
          className="h-56 animate-pulse rounded-md bg-muted"
          aria-hidden
          data-testid="dashboard-loading"
        />
      ) : error ? (
        <p className="py-8 text-sm text-destructive" data-testid="dashboard-error">
          {t('dashboard.loadError', 'Nutzungsdaten konnten nicht geladen werden.')}
        </p>
      ) : (
        <UsageChart
          data={chartData}
          seriesColors={seriesColors}
          height={220}
          granularity={granularity}
          startDate={startDate}
          endDate={endDate}
          noDataText={t(
            'dashboard.empty',
            'Noch keine Nutzung im Zeitraum — der erste API-Aufruf erscheint hier.'
          )}
          totalLabel={t('dashboard.chartTotal', 'Gesamt')}
          // Auf dem Dashboard kostet die Legende eine Zeile, die das Diagramm
          // besser gebrauchen kann — und der Tooltip nennt beim Überfahren
          // ohnehin jede Serie. Auf der /usage-Seite bleibt sie offen.
          collapsibleLegend
          legendLabel={t('dashboard.chartLegend', 'Legende')}
          className={chartData.length === 0 ? 'py-10' : undefined}
        />
      )}

      {/* Quota details live on /usage — link instead of duplicating them */}
      <div className="flex">
        <Link
          to="/usage"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-sm text-sm text-primary hover:text-[var(--primary-hover)]',
            CONTROL_TRANSITION,
            FIELD_RING
          )}
        >
          {t('dashboard.toUsage', 'Verbrauch & Kontingente ansehen')}
          <ArrowRight aria-hidden className="h-4 w-4" />
        </Link>
      </div>
    </div>
  )
}
